import { Link } from 'react-router-dom';
import type { Shoot } from '../../api/types';
import { imageUrl } from '../../api/client';
import { UserChip } from '../ui/UserChip';

interface ShootCardProps {
  shoot: Shoot;
}

// Formaterar ISO-datum till "12 APR 2026" (matchar prototypen).
function formatDate(input?: string | null): string {
  if (!input) return '';
  // Backend kan skicka "2026-04-12" eller fullständigt ISO
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return input.toUpperCase();
  return d
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .toUpperCase();
}

// Collage-card för shoot-listan.
// Layout: stor porträtt-bild till vänster (1.6fr) + två landscape-bilder
// staplade till höger. Sista right-bilden får "+N"-pillen om shooten har
// fler bilder än 3.
export function ShootCard({ shoot }: ShootCardProps) {
  const collage = shoot.collageImageIds ?? [];
  const cover = shoot.coverImageId ?? collage[0] ?? null;

  // Vi visar tre bilder; om backend bara gett oss cover faller vi tillbaka
  // till att visa cover ensamt i mainen och lämna sidorna gråa.
  const main = cover ?? collage[0];
  const side1 = collage[1] ?? collage[0] ?? cover;
  const side2 = collage[2] ?? collage[1] ?? cover;

  const totalFrames = shoot.imageCount ?? 0;
  const remainder = Math.max(0, totalFrames - 3);

  const hasFavorites = (shoot.favoriteCount ?? 0) > 0;

  return (
    <Link
      to={`/shoots/${shoot.id}`}
      className="group block text-loupe-text no-underline transition-transform hover:-translate-y-1 duration-300"
    >
      <div className="grid grid-cols-[1.6fr_1fr] gap-1.5 mb-5 h-[280px] sm:h-[320px]">
        {/* Main */}
        <div className="row-span-2 rounded-2xl overflow-hidden bg-loupe-bg-2 relative aspect-stable">
          {main ? (
            <img
              src={imageUrl(main, 'preview')}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
          ) : null}
        </div>
        {/* Side top */}
        <div className="rounded-2xl overflow-hidden bg-loupe-bg-2 relative aspect-stable">
          {side1 ? (
            <img
              src={imageUrl(side1, 'thumb')}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
          ) : null}
        </div>
        {/* Side bottom */}
        <div className="rounded-2xl overflow-hidden bg-loupe-bg-2 relative aspect-stable">
          {side2 ? (
            <img
              src={imageUrl(side2, 'thumb')}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
          ) : null}
          {remainder > 0 ? (
            <div
              className={[
                'absolute bottom-2.5 right-2.5',
                'px-2.5 py-1 rounded-full text-[10px] font-medium tracking-[0.1em]',
                'bg-black/60 backdrop-blur border border-white/10 text-white/90',
                'pointer-events-none',
              ].join(' ')}
            >
              +{remainder}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex justify-between items-baseline mb-2.5 gap-3">
        <div className="font-display text-2xl sm:text-[26px] font-medium tracking-tight leading-[1.1]">
          {shoot.name}
        </div>
        <div className="text-[11px] text-loupe-text-3 tracking-[0.15em] uppercase whitespace-nowrap tabular-nums">
          {formatDate(shoot.date ?? shoot.shootDate ?? shoot.createdAt)}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3.5 text-xs text-loupe-text-2 tabular-nums">
        <span className="inline-flex items-center gap-1.5">
          <span className="text-loupe-text">{totalFrames}</span>
          <span className="text-loupe-text-3">frames</span>
        </span>
        {hasFavorites ? (
          <>
            <span className="w-px h-2.5 bg-loupe-border-2" />
            <span className="inline-flex items-center gap-1.5">
              <span className="text-heart text-[11px]">♥</span>
              <span>{shoot.favoriteCount}</span>
            </span>
          </>
        ) : null}
        <span className="inline-flex items-center gap-1 ml-auto">
          <UserChip user="henrik" size="sm" />
          <UserChip user="michelle" size="sm" />
        </span>
      </div>
    </Link>
  );
}
